(() => {
  // A layered garment illustration, not a pattern or fit simulation.
  const W = 418, H = 627;
  const imageCache = new Map();
  const maskCache = new Map();
  const boundsCache = new Map();
  const labelURL = 'https://cdn.shopify.com/s/files/1/0608/4087/0121/files/vm-bespoke-labels-sizing-17.png?v=1792533750';
  const loadImage = url => {
    if (!imageCache.has(url)) imageCache.set(url, new Promise((resolve, reject) => {
      const image = new Image(); image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image); image.onerror = reject; image.src = url;
    }));
    return imageCache.get(url);
  };
  // Landmarks in the authored render sheets; each sheet is three fabrics by two views.
  const geometry = {
    slim: {front:[[135,335],[103,318],[82,289]],back:[[130,339],[103,318],[82,291]],button:[[240,43],[214,43],[192,43]],rivets:[[156,54],[98,82],[259,54],[323,82],[105,58],[137,61]]},
    straight:{front:[[131,335],[101,319],[88,291]],back:[[132,337],[103,319],[87,292]],button:[[237,43],[214,43],[197,42]],rivets:[[156,54],[97,82],[257,54],[322,83],[105,57],[137,61]]},
    tapered:{front:[[134,339],[100,317],[75,284]],back:[[131,339],[101,319],[74,290]],button:[[241,39],[214,39],[186,38]],rivets:[[156,49],[96,79],[257,49],[322,80],[104,55],[137,57]]},
    wide:{front:[[135,315],[113,305],[104,290]],back:[[134,321],[112,307],[103,291]],button:[[229,34],[214,34],[201,35]],rivets:[[162,45],[105,75],[254,45],[312,76],[116,50],[141,53]]},
    bootcut:{front:[[146,333],[111,306],[81,271]],back:[[142,338],[110,307],[79,276]],button:[[244,40],[214,40],[183,40]],rivets:[[160,50],[106,76],[257,50],[316,78],[117,52],[141,57]]},
    baggy:{front:[[153,347],[107,313],[71,267]],back:[[154,345],[110,313],[72,270]],button:[[255,38],[214,38],[176,37]],rivets:[[157,48],[97,76],[256,48],[318,79],[108,52],[138,57]]}
  };
  const palette = value => /copper/i.test(value) ? ['#e0af85','#aa643e','#573224'] : /glossy/i.test(value) ? ['#a6a6a6','#171719','#050506'] : /black/i.test(value) ? ['#555558','#242427','#101012'] : ['#b7b8b5','#747a7b','#353d40'];
  const metal = (ctx,x,y,r,value,perforated=false) => {
    const colors=palette(value), grad=ctx.createRadialGradient(x-r*.35,y-r*.4,r*.08,x,y,r);
    grad.addColorStop(0,colors[0]); grad.addColorStop(.48,colors[1]); grad.addColorStop(1,colors[2]);
    ctx.fillStyle='rgba(0,0,0,.3)'; ctx.beginPath();ctx.ellipse(x+.3,y+.8,r+.6,r*.93,0,0,Math.PI*2);ctx.fill();
    ctx.fillStyle=grad;ctx.beginPath();ctx.ellipse(x,y,r,r*.93,0,0,Math.PI*2);ctx.fill();
    ctx.strokeStyle=colors[0];ctx.lineWidth=.45;ctx.beginPath();ctx.ellipse(x,y,r*.82,r*.76,0,Math.PI,Math.PI*1.8);ctx.stroke();
    if(perforated){ctx.fillStyle='#171c1c';for(const [dx,dy] of [[0,0],[-1.7,-1.5],[1.7,-1.5],[-1.7,1.5],[1.7,1.5]]){ctx.beginPath();ctx.arc(x+dx,y+dy,.52,0,Math.PI*2);ctx.fill();}}
  };
  const crop = (ctx,image,column,row) => ctx.drawImage(image,column*image.width/3,row*image.height/2,image.width/3,image.height/2,0,0,W,H);
  const boundsFor = (image,url,column,row) => {
    const key=[url,column,row].join('|');
    if(boundsCache.has(key))return boundsCache.get(key);
    const layer=document.createElement('canvas');layer.width=W;layer.height=H;
    const context=layer.getContext('2d',{willReadFrequently:true});crop(context,image,column,row);
    const pixels=context.getImageData(0,0,W,H).data;
    const columns=new Uint16Array(W),rows=new Uint16Array(H);
    for(let y=0;y<H;y++)for(let x=0;x<W;x++)if(pixels[(y*W+x)*4+3]>220){columns[x]++;rows[y]++;}
    const left=columns.findIndex(n=>n>12),right=columns.findLastIndex(n=>n>12);
    const top=rows.findIndex(n=>n>100),bottom=rows.findLastIndex(n=>n>100);
    const bounds=left<0?{left:0,top:0,width:W,height:H}:{left,top,width:right-left+1,height:bottom-top+1};
    boundsCache.set(key,bounds);return bounds;
  };
  const present = (context,layer,image,url,column,row,g) => {
    const bounds=boundsFor(image,url,column,row),reference=boundsFor(image,url,1,0);
    const height=580,waist=g[row?'back':'front'][column],target=g.front[1];
    const sx=(target[1]-target[0])/(waist[1]-waist[0])*height/reference.height,sy=height/bounds.height;
    context.clearRect(0,0,W,H);
    context.drawImage(layer,W/2-(waist[0]+waist[1])/2*sx,20-bounds.top*sy,W*sx,H*sy);
  };
  const threadMask = (image,url,row) => {
    const key=url+':'+row;
    if(maskCache.has(key)) return maskCache.get(key);
    const layer=document.createElement('canvas');layer.width=W;layer.height=H;
    const context=layer.getContext('2d',{willReadFrequently:true});crop(context,image,1,row);
    const data=context.getImageData(0,0,W,H),mask=new Uint8ClampedArray(W*H);
    for(let p=0,i=0;p<mask.length;p++,i+=4){
      const r=data.data[i],green=data.data[i+1],b=data.data[i+2];
      if(data.data[i+3]>220 && r-green>1 && r-b>-12){
        mask[p]=Math.min(255,Math.max(0,(r-green-1)*85));
      }
    }
    const stitch=new Uint8Array(mask.length);
    for(let p=0;p<mask.length;p++)stitch[p]=mask[p]>160?1:0;
    const offsets=[-W,-W+1,1,W+1,W,W-1,-1,-W-1];
    for(let pass=0;pass<30;pass++){
      let changed=false;
      for(let phase=0;phase<2;phase++){
        const remove=[];
        for(let y=1;y<H-1;y++)for(let x=1;x<W-1;x++){
          const p=y*W+x;if(!stitch[p])continue;
          const n=offsets.map(offset=>stitch[p+offset]),count=n.reduce((a,b)=>a+b,0);
          if(count<2||count>6)continue;
          let changes=0;for(let j=0;j<8;j++)if(!n[j]&&n[(j+1)%8])changes++;
          if(changes!==1)continue;
          if(phase===0 ? n[0]*n[2]*n[4]||n[2]*n[4]*n[6] : n[0]*n[2]*n[6]||n[0]*n[4]*n[6])continue;
          remove.push(p);
        }
        if(remove.length)changed=true;
        remove.forEach(p=>stitch[p]=0);
      }
      if(!changed)break;
    }
    const cloth=new Uint8ClampedArray(data.data);
    for(let p=0;p<mask.length;p++){
      if(!mask[p])continue;
      const x=p%W,y=Math.floor(p/W),sum=[0,0,0];let count=0;
      for(let dy=-4;dy<=4;dy++)for(let dx=-4;dx<=4;dx++){
        if(x+dx<0||x+dx>=W||y+dy<0||y+dy>=H)continue;
        const q=(y+dy)*W+x+dx,j=q*4;
        if(mask[q]||data.data[j+3]<240)continue;
        for(let c=0;c<3;c++)sum[c]+=data.data[j+c];count++;
      }
      if(count)for(let c=0;c<3;c++)cloth[p*4+c]=sum[c]/count;
    }
    const result={mask:stitch,data,cloth};maskCache.set(key,result);return result;
  };
  const drawFabric = (ctx,image,url,row,column,color) => {
    const {mask,data,cloth:base}=threadMask(image,url,row),pixels=ctx.createImageData(W,H);
    const rgb=color?.match(/[a-f\d]{2}/gi)?.map(v=>parseInt(v,16));
    for(let p=0,i=0;p<mask.length;p++,i+=4){
      const r=base[i],green=base[i+1],b=base[i+2];
      const light=r*.2126+green*.7152+b*.0722;
      let cloth=[r,green,b];
      if(column===0){
        const black=Math.max(4,Math.min(52,light*.48));
        cloth=[black*.97,black*.98,black];
      }else if(column===2){
        const natural=Math.max(105,Math.min(250,204+light*.43));
        cloth=[natural, natural*.963,natural*.89];
      }
      const amount=rgb?mask[p]*.92:0;
      const threadLight=.76+Math.min(r,180)/600;
      for(let c=0;c<3;c++) pixels.data[i+c]=cloth[c]*(1-amount)+(rgb?Math.min(255,rgb[c]*threadLight):0)*amount;
      pixels.data[i+3]=data.data[i+3];
    }
    ctx.putImageData(pixels,0,0);
  };
  const drawLabel = (ctx,image,choice,g,column) => {
    if(!choice)return;
    const black=choice.startsWith('black'),wordmark=choice.endsWith('wordmark');
    const bounds=g.back[column],width=58,height=35,x=bounds[1]-width-9,y=19;
    ctx.save();ctx.shadowColor='rgba(0,0,0,.24)';ctx.shadowBlur=1.6;ctx.shadowOffsetY=.8;
    ctx.drawImage(image,black?654:32,wordmark?750:168,570,wordmark?341:360,x,y,width,height);ctx.restore();
  };
  const initialize = root => {
    const panel = root.querySelector('[data-jeans-preview]');
    if (!panel || panel.dataset.initialized) return;
    panel.dataset.initialized = 'true';
    const form = root.querySelector('form'), render = panel.querySelector('[data-preview-render]'), assets = panel.querySelector('[data-preview-assets]');
    const ctx=render.getContext('2d');
    const chosen = group => form.querySelector('[data-preview-group="' + group + '"]:checked');
    let view='front', revision=0, lastSignature='',cardRevision=0,lastColumn=-1;
    const update = () => {
      const fabric=form.querySelector('[data-bespoke-color]:checked'),fit=form.querySelector('[data-bespoke-fit]:checked');
      const fitKey=fit?.dataset.fit || 'straight',source=assets.querySelector('[data-render-fit="'+fitKey+'"]');
      const cloth=fabric?.dataset.fabric || 'Raw Indigo',column=/natural/i.test(cloth)?2:/black/i.test(cloth)?0:1,url=source?.dataset.renderUrl;
      const selected=Object.fromEntries(['button','thread','rivet','fly'].map(group=>[group,chosen(group)]));
      const label=form.querySelector('[data-leather-label]:checked');
      const viewLabel=panel.querySelector('[data-preview-view="'+view+'"]').textContent;
      const parts=[fit?.value || 'Straight',cloth,viewLabel];
      if(column!==lastColumn){
        lastColumn=column;const cardTicket=++cardRevision;
        form.querySelectorAll('[data-silhouette-fit]').forEach(card=>{
          const cardURL=assets.querySelector('[data-render-fit="'+card.dataset.silhouetteFit+'"]').dataset.renderUrl;
          loadImage(cardURL).then(sheet=>{
            if(cardTicket!==cardRevision)return;
            const layer=document.createElement('canvas');layer.width=W;layer.height=H;
            drawFabric(layer.getContext('2d'),sheet,cardURL,0,column,column===0?'#080808':column===2?'#b59b76':'#bc8a43');
            present(card.getContext('2d'),layer,sheet,cardURL,1,0,geometry[card.dataset.silhouetteFit]);
          }).catch(()=>{});
        });
      }
      render.setAttribute('aria-label',parts.concat(Object.values(selected).map(v=>v?.value).filter(Boolean),label?.value||[]).join(' · '));
      panel.querySelector('[data-preview-description]').textContent=parts.join(' · ');
      panel.querySelector('[data-preview-materials]').textContent=[selected.button?.value,selected.rivet?.value&&selected.rivet.value+' rivets',selected.thread?.value&&selected.thread.value+' stitching',label?.value].filter(Boolean).join(' · ');
      panel.querySelector('[data-preview-dye-note]').hidden=!form.querySelector('[data-dye-custom]').checked;
      for(const group of ['button','thread','rivet','fly']){
        const input=selected[group],figure=panel.querySelector('[data-preview-swatch="'+group+'"]'),image=input?.closest('.vm-option').querySelector('img');figure.hidden=!image;
        if(image){const img=figure.querySelector('img'),src=image.currentSrc||image.src;if(img.getAttribute('src')!==src)img.src=src;img.alt=input.value;figure.querySelector('strong').textContent=input.value;}
      }
      const signature=[fitKey,column,view,selected.thread?.value,selected.button?.value,selected.rivet?.value,label?.value].join('|');
      if(signature===lastSignature || !url)return;
      lastSignature=signature;const ticket=++revision;
      Promise.all([loadImage(url),label?loadImage(labelURL):Promise.resolve(null)]).then(([sheet,leather])=>{
        if(ticket!==revision)return;
        const row=view==='back'?1:0,g=geometry[fitKey];
        const layer=document.createElement('canvas');layer.width=W;layer.height=H;
        const paint=layer.getContext('2d');
        drawFabric(paint,sheet,url,row,column,selected.thread?.dataset.previewColor || (column===0?'#080808':column===2?'#b59b76':'#bc8a43'));
        if(!row){
          if(selected.button)metal(paint,...g.button[1],5.2,selected.button.value,/perforated/i.test(selected.button.value));
          if(selected.rivet){const from=g.front[1],to=g.front[1],scale=(to[1]-to[0])/(from[1]-from[0]);
            g.rivets.forEach(([x,y])=>metal(paint,to[0]+(x-from[0])*scale,y+0,2.5,selected.rivet.value));}
        }else if(leather)drawLabel(paint,leather,label.dataset.leatherLabel,g,1);
        present(ctx,layer,sheet,url,1,row,g);
        render.dataset.rendered=signature;
      }).catch(()=>{lastSignature='';panel.querySelector('[data-preview-description]').textContent=panel.dataset.error;});
    };
    panel.dataset.error=panel.querySelector('[data-preview-error]').textContent;
    panel.querySelectorAll('[data-preview-view]').forEach(button=>button.addEventListener('click',()=>{
      view=button.dataset.previewView;panel.querySelectorAll('[data-preview-view]').forEach(item=>item.setAttribute('aria-pressed',String(item===button)));update();
    }));
    const zoom=panel.querySelector('[data-preview-zoom]');zoom.addEventListener('click',()=>{
      const active=zoom.getAttribute('aria-pressed')!=='true';zoom.setAttribute('aria-pressed',String(active));panel.classList.toggle('is-detail',active);
    });
    root.addEventListener('vm:bespoke:update',update);form.addEventListener('change',update);update();
  };
  const initializeAll=parent=>parent.querySelectorAll('[data-vm-bespoke]').forEach(initialize);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>initializeAll(document));else initializeAll(document);
  document.addEventListener('shopify:section:load',event=>initializeAll(event.target));
})();

